import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const AdminResetPassword = () => {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [recoveryValid, setRecoveryValid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Set New Password | Hadassah Homes Admin";
  }, []);

  useEffect(() => {
    // Supabase parses the recovery hash automatically and emits PASSWORD_RECOVERY
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && session)) {
        setRecoveryValid(true);
        setReady(true);
      }
    });

    // Fallback: check for an existing session in case the event already fired
    supabase.auth.getSession().then(({ data }) => {
      const isRecovery =
        window.location.hash.includes("type=recovery") ||
        window.location.search.includes("type=recovery");
      if (data.session && isRecovery) {
        setRecoveryValid(true);
      }
      setReady(true);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    const { error: updErr } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (updErr) {
      setError(updErr.message);
      toast.error("Could not update password");
      return;
    }
    toast.success("Password updated");
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0F1F18] px-5 py-16">
      <div className="w-full max-w-md border border-white/10 bg-white p-8 md:p-10">
        <div className="mb-7">
          <p className="font-body text-[10px] uppercase tracking-[0.28em] text-accent">
            Hadassah Homes
          </p>
          <h1 className="mt-2 font-headline text-3xl text-foreground">Set a new password</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Choose a strong password of at least 8 characters.
          </p>
        </div>

        {!ready ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !recoveryValid ? (
          <div className="space-y-5">
            <div className="border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              This password reset link is invalid or has expired. Please request a new one.
            </div>
            <Link
              to="/admin/forgot-password"
              className="inline-block text-sm text-accent hover:text-accent/80"
            >
              Request a new reset link
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="mb-2 block text-xs uppercase tracking-[0.16em] text-muted-foreground">
                New password
              </label>
              <Input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-none"
              />
            </div>
            <div>
              <label className="mb-2 block text-xs uppercase tracking-[0.16em] text-muted-foreground">
                Confirm password
              </label>
              <Input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="rounded-none"
              />
            </div>

            {error ? <p className="text-sm text-destructive">{error}</p> : null}

            <Button
              type="submit"
              variant="gold"
              size="lg"
              disabled={submitting}
              className="w-full rounded-none"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>

            <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Lock className="h-3 w-3" /> Secured by Supabase Auth
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminResetPassword;
